import { getCurrentUser } from 'aws-amplify/auth';
import {
  listFavorites as apiListFavorites,
  addFavorite as apiAddFavorite,
  removeFavorite as apiRemoveFavorite,
} from '@/lib/favorites';
import { createReport, listReports as apiListReports } from '@/lib/accountsReports';
import { listViews as apiListViews } from '@/lib/views';
import {
  buildUserDataSyncSummary,
  normalizeSyncState,
  type SyncMetadata,
  type SyncState,
  type UserDataSyncSummary,
} from '@/lib/activitySync';
import { trackActivityMetric } from '@/lib/activityTelemetry';
import { trackCoreJourneyEvent } from '@/lib/coreJourneyAnalytics';
import {
  DEFAULT_COUNCIL_SLUG,
  applicationIdentityKey,
  getCouncilConfig,
  type CouncilSlug,
} from '@/lib/councils';
import { normalizeRef } from '@/lib/utils/normalizeRef';

export interface UserFavorite extends SyncMetadata {
  id: string;
  reference_no: string;
  application_uid?: string | null;
  source_council?: string | null;
  source_system?: string | null;
  address: string;
  category?: string;
  primaryCategory?: string;
  favorited_at: string;
  application_data?: unknown;
}

export interface UserReport extends SyncMetadata {
  id: string;
  reference_no: string;
  application_uid?: string | null;
  source_council?: string | null;
  source_system?: string | null;
  address: string;
  report: string;
  generated_at: string;
  application_data?: unknown;
  metadata?: Record<string, unknown> | null;
  title?: string;
  kind?: string;
}

export interface UserView extends SyncMetadata {
  id: string;
  reference_no: string;
  application_uid?: string | null;
  source_council?: string | null;
  source_system?: string | null;
  address: string;
  category?: string;
  scale_rating?: number;
  view_count: number;
  viewed_at: string;
}

export interface UserSettings {
  theme?: string;
  mapStyle?: string;
  displayMode?: string;
  iconStyle?: string;
  notifications?: boolean;
  [key: string]: unknown;
}

export interface UserData {
  favorites: UserFavorite[];
  reports: UserReport[];
  views: UserView[];
  settings: UserSettings;
  lastSynced: string;
}

interface SyncOptions {
  forceServerState?: boolean;
}

interface PersistGeneratedReportInput {
  reference_no: string;
  address: string;
  report: string;
  application_data?: unknown;
  metadata?: Record<string, unknown>;
  title?: string;
  kind?: string;
}

interface PersistGeneratedReportResult {
  report: UserReport;
  syncState: SyncState;
  savedToAccount: boolean;
  message: string;
}

const USE_API = !!process.env.NEXT_PUBLIC_API_URL;

const asRecord = (value: unknown): Record<string, unknown> | null =>
  typeof value === 'object' && value !== null ? (value as Record<string, unknown>) : null;

const asString = (value: unknown): string | undefined =>
  typeof value === 'string' ? value : undefined;

const asFiniteNumber = (value: unknown): number | undefined =>
  typeof value === 'number' && Number.isFinite(value) ? value : undefined;

const errorStatus = (error: unknown): number | undefined => {
  const status = asRecord(error)?.status;
  return typeof status === 'number' ? status : undefined;
};

const errorMessage = (error: unknown): string | undefined => {
  const message = asRecord(error)?.message;
  return typeof message === 'string' ? message : undefined;
};

type AccountSyncFallbackTelemetry = {
  state: SyncState;
  action: 'saved_local' | 'sync_failed';
  authenticated: boolean;
  metadata: Record<string, unknown>;
};

export const classifyAccountSyncFallback = (error: unknown): AccountSyncFallbackTelemetry => {
  const status = errorStatus(error);
  const message = errorMessage(error);

  if (status === 401) {
    return {
      state: 'local_only',
      action: 'saved_local',
      authenticated: false,
      metadata: {
        fallback_reason: 'auth_session_unavailable',
        sync_error_status: status,
      },
    };
  }

  return {
    state: 'failed',
    action: 'sync_failed',
    authenticated: true,
    metadata: {
      fallback_reason: 'account_sync_failed',
      sync_error_status: typeof status === 'number' ? status : 'unknown',
      sync_error_message: message || 'Unknown error',
    },
  };
};

const normalizeTimestamp = (value: unknown, fallback: string): string => {
  const text = asString(value);
  return text && text.trim() ? text : fallback;
};

const isAccountLikeId = (value: string | undefined, prefixes: string[]): boolean =>
  Boolean(value && prefixes.some(prefix => value.startsWith(prefix)));

export async function isUserAuthenticated(): Promise<boolean> {
  try {
    const user = await getCurrentUser();
    return !!(user?.userId || user?.username);
  } catch {
    return false;
  }
}

class UserDataService {
  private syncInProgress = false;
  private isHydratingFavorites = false;

  private normalizeFavoriteRecord(value: unknown, index: number): UserFavorite | null {
    const record = asRecord(value);
    if (!record) return null;

    const normalizedRef = normalizeRef(
      String(record.reference_no ?? record.ref ?? record.id ?? '').trim()
    );
    if (!normalizedRef) return null;

    const rawId = asString(record.id);
    const inferredAccount = isAccountLikeId(rawId, ['api_fav_']) || Boolean(record.account_id);
    const syncState = normalizeSyncState(record.sync_state, inferredAccount ? 'account' : 'local_only');
    const localId = asString(record.local_id) || (!inferredAccount ? rawId || `fav_${normalizedRef}_${index}` : undefined);
    const id = rawId || localId || `fav_${normalizedRef}_${index}`;

    return {
      id,
      local_id: localId,
      account_id: asString(record.account_id),
      sync_state: inferredAccount ? 'account' : syncState,
      last_sync_error: asString(record.last_sync_error) || null,
      last_sync_attempt_at: asString(record.last_sync_attempt_at),
      synced_at: asString(record.synced_at),
      reference_no: normalizedRef,
      application_uid: asString(record.application_uid),
      source_council: asString(record.source_council) || DEFAULT_COUNCIL_SLUG,
      source_system: asString(record.source_system) || 'idox',
      address: asString(record.address) || '',
      category: asString(record.category),
      primaryCategory: asString(record.primaryCategory),
      favorited_at: normalizeTimestamp(record.favorited_at ?? record.created_at, new Date().toISOString()),
      application_data: record.application_data,
    };
  }

  private normalizeReportRecord(value: unknown, index: number): UserReport | null {
    const record = asRecord(value);
    if (!record) return null;

    const rawReference = String(record.reference_no ?? record.ref ?? '').trim();
    const normalizedRef = normalizeRef(rawReference) || rawReference;
    const rawId = asString(record.id);
    const accountId = asString(record.account_id);
    const inferredAccount = Boolean(accountId || isAccountLikeId(rawId, ['api_report_']));
    const syncState = normalizeSyncState(record.sync_state, inferredAccount ? 'account' : 'local_only');
    const localId = asString(record.local_id) || (!inferredAccount ? rawId || `report_${normalizedRef || 'unknown'}_${index}` : undefined);
    const id = accountId || rawId || localId || `report_${normalizedRef || 'unknown'}_${index}`;

    return {
      id,
      local_id: localId,
      account_id: accountId,
      sync_state: accountId ? 'account' : syncState,
      last_sync_error: asString(record.last_sync_error) || null,
      last_sync_attempt_at: asString(record.last_sync_attempt_at),
      synced_at: asString(record.synced_at),
      reference_no: normalizedRef,
      application_uid: asString(record.application_uid),
      source_council: asString(record.source_council) || DEFAULT_COUNCIL_SLUG,
      source_system: asString(record.source_system) || 'idox',
      address: asString(record.address) || '',
      report:
        asString(record.report) ||
        asString(record.report_text) ||
        asString(record.preview) ||
        '',
      generated_at: normalizeTimestamp(record.generated_at ?? record.created_at, new Date().toISOString()),
      application_data: record.application_data,
      metadata: asRecord(record.metadata),
      title: asString(record.title),
      kind: asString(record.kind),
    };
  }

  private normalizeViewRecord(value: unknown, index: number): UserView | null {
    const record = asRecord(value);
    if (!record) return null;

    const rawReference = String(record.reference_no ?? record.ref ?? record.id ?? '').trim();
    const normalizedRef = normalizeRef(rawReference) || rawReference;
    if (!normalizedRef) return null;

    const rawId = asString(record.id);
    const inferredAccount = Boolean(record.account_id || isAccountLikeId(rawId, ['api_view_']));
    const syncState = normalizeSyncState(record.sync_state, inferredAccount ? 'account' : 'local_only');
    const localId = asString(record.local_id) || (!inferredAccount ? rawId || `view_${normalizedRef}_${index}` : undefined);
    const id = rawId || localId || `view_${normalizedRef}_${index}`;
    const numericViewCount = asFiniteNumber(record.view_count);
    const fallbackViewCount = Number(record.view_count);

    return {
      id,
      local_id: localId,
      account_id: asString(record.account_id),
      sync_state: inferredAccount ? 'account' : syncState,
      last_sync_error: asString(record.last_sync_error) || null,
      last_sync_attempt_at: asString(record.last_sync_attempt_at),
      synced_at: asString(record.synced_at),
      reference_no: normalizedRef,
      address: asString(record.address) || '',
      category: asString(record.category),
      scale_rating: asFiniteNumber(record.scale_rating),
      view_count:
        numericViewCount ??
        (Number.isFinite(fallbackViewCount) && fallbackViewCount > 0 ? fallbackViewCount : 1),
      viewed_at: normalizeTimestamp(
        record.viewed_at ?? record.last_viewed_at ?? record.first_viewed_at,
        new Date().toISOString()
      ),
    };
  }

  private loadLocalData(): UserData {
    if (typeof window === 'undefined') {
      return { favorites: [], reports: [], views: [], settings: {}, lastSynced: '' };
    }

    try {
      const favoritesRaw = JSON.parse(localStorage.getItem('cs_favorites') || '[]');
      const reportsRaw = JSON.parse(localStorage.getItem('planning_reports') || '[]');
      const viewsRaw = JSON.parse(localStorage.getItem('cs_views') || '[]');
      const settings = JSON.parse(localStorage.getItem('user_settings') || '{}');
      const lastSynced = localStorage.getItem('last_sync') || '';

      const favorites = Array.isArray(favoritesRaw)
        ? favoritesRaw
            .map((favorite, index) => this.normalizeFavoriteRecord(favorite, index))
            .filter((favorite): favorite is UserFavorite => Boolean(favorite))
        : [];

      const reports = Array.isArray(reportsRaw)
        ? reportsRaw
            .map((report, index) => this.normalizeReportRecord(report, index))
            .filter((report): report is UserReport => Boolean(report))
        : [];

      const views = Array.isArray(viewsRaw)
        ? viewsRaw
            .map((view, index) => this.normalizeViewRecord(view, index))
            .filter((view): view is UserView => Boolean(view))
        : [];

      return { favorites, reports, views, settings, lastSynced };
    } catch (error) {
      console.error('Failed to load local data:', error);
      return { favorites: [], reports: [], views: [], settings: {}, lastSynced: '' };
    }
  }

  private saveLocalData(update: Partial<UserData>): void {
    if (typeof window === 'undefined') return;

    try {
      if (update.favorites) {
        localStorage.setItem('cs_favorites', JSON.stringify(update.favorites));
      }
      if (update.reports) {
        localStorage.setItem('planning_reports', JSON.stringify(update.reports));
      }
      if (update.views) {
        localStorage.setItem('cs_views', JSON.stringify(update.views));
      }
      if (update.settings) {
        localStorage.setItem('user_settings', JSON.stringify(update.settings));
      }
      if (update.lastSynced) {
        localStorage.setItem('last_sync', update.lastSynced);
      }
    } catch (error) {
      console.error('Failed to save local data:', error);
    }
  }

  private dispatchFavoritesUpdated(favorites: UserFavorite[]): void {
    if (typeof window === 'undefined') return;
    try {
      window.dispatchEvent(new CustomEvent('favoritesUpdated', { detail: favorites }));
    } catch {
      // ignore
    }
  }

  private dispatchReportsUpdated(reports: UserReport[]): void {
    if (typeof window === 'undefined') return;
    try {
      window.dispatchEvent(new CustomEvent('reportsUpdated', { detail: reports }));
    } catch {
      // ignore
    }
  }

  private dispatchViewsUpdated(views: UserView[]): void {
    if (typeof window === 'undefined') return;
    try {
      window.dispatchEvent(new CustomEvent('viewsUpdated', { detail: views }));
    } catch {
      // ignore
    }
  }

  private dispatchSettingsUpdated(settings: UserSettings): void {
    if (typeof window === 'undefined') return;
    try {
      window.dispatchEvent(new CustomEvent('settingsUpdated', { detail: settings }));
    } catch {
      // ignore
    }
  }

  private saveFavorites(favorites: UserFavorite[]): void {
    this.saveLocalData({ favorites });
    this.dispatchFavoritesUpdated(favorites);
  }

  private saveReports(reports: UserReport[]): void {
    this.saveLocalData({ reports });
    this.dispatchReportsUpdated(reports);
  }

  private saveViews(views: UserView[]): void {
    this.saveLocalData({ views });
    this.dispatchViewsUpdated(views);
  }

  private councilScope(value?: string | null): CouncilSlug {
    return getCouncilConfig(value)?.slug || DEFAULT_COUNCIL_SLUG;
  }

  private favoriteKey(
    referenceOrFavorite: string | Pick<UserFavorite, 'reference_no' | 'application_uid' | 'source_council'>
  ): string {
    if (typeof referenceOrFavorite !== 'string') {
      const identity = applicationIdentityKey(referenceOrFavorite as unknown as Record<string, unknown>);
      if (identity) return identity;
      return `${this.councilScope(referenceOrFavorite.source_council)}:${normalizeRef(referenceOrFavorite.reference_no) || referenceOrFavorite.reference_no}`;
    }
    return `${DEFAULT_COUNCIL_SLUG}:${normalizeRef(referenceOrFavorite) || referenceOrFavorite}`;
  }

  private reportKey(report: Pick<UserReport, 'id' | 'account_id' | 'local_id' | 'reference_no' | 'generated_at'>): string {
    return (
      report.account_id ||
      report.local_id ||
      report.id ||
      `${normalizeRef(report.reference_no) || report.reference_no}:${report.generated_at}`
    );
  }

  private viewKey(referenceOrView: string | Pick<UserView, 'reference_no' | 'application_uid' | 'source_council'>): string {
    if (typeof referenceOrView !== 'string') {
      const identity = applicationIdentityKey(referenceOrView as unknown as Record<string, unknown>);
      if (identity) return identity;
      return `${this.councilScope(referenceOrView.source_council)}:${normalizeRef(referenceOrView.reference_no) || referenceOrView.reference_no}`;
    }
    return `${DEFAULT_COUNCIL_SLUG}:${normalizeRef(referenceOrView) || referenceOrView}`;
  }

  private sortFavorites(favorites: UserFavorite[]): UserFavorite[] {
    return [...favorites].sort(
      (left, right) => new Date(right.favorited_at).getTime() - new Date(left.favorited_at).getTime()
    );
  }

  private sortReports(reports: UserReport[]): UserReport[] {
    return [...reports].sort(
      (left, right) => new Date(right.generated_at).getTime() - new Date(left.generated_at).getTime()
    );
  }

  private sortViews(views: UserView[]): UserView[] {
    return [...views]
      .sort((left, right) => new Date(right.viewed_at).getTime() - new Date(left.viewed_at).getTime())
      .slice(0, 1000);
  }

  private upsertFavorite(favorites: UserFavorite[], favorite: UserFavorite): UserFavorite[] {
    const key = this.favoriteKey(favorite);
    const filtered = favorites.filter(item => this.favoriteKey(item) !== key);
    return this.sortFavorites([...filtered, favorite]);
  }

  private updateFavoriteState(
    referenceNo: string,
    updater: (favorite: UserFavorite) => UserFavorite,
    council = DEFAULT_COUNCIL_SLUG
  ): UserFavorite[] {
    const key = this.favoriteKey({ reference_no: referenceNo, source_council: council });
    const localData = this.loadLocalData();
    const updated = localData.favorites.map(favorite =>
      this.favoriteKey(favorite) === key ? updater(favorite) : favorite
    );
    this.saveFavorites(updated);
    return updated;
  }

  private upsertReport(reports: UserReport[], report: UserReport): UserReport[] {
    const key = this.reportKey(report);
    const referenceKey = normalizeRef(report.reference_no) || report.reference_no;
    const filtered = reports.filter(existing => {
      if (this.reportKey(existing) === key) return false;
      if (report.local_id && existing.local_id === report.local_id) return false;
      if (report.account_id && existing.account_id === report.account_id) return false;
      if (report.id && existing.id === report.id) return false;
      if (
        referenceKey &&
        (normalizeRef(existing.reference_no) || existing.reference_no) === referenceKey &&
        (existing.kind || 'property') === (report.kind || 'property')
      ) {
        return false;
      }
      return true;
    });
    return this.sortReports([...filtered, report]);
  }

  private updateReportState(identifier: string, updater: (report: UserReport) => UserReport): UserReport[] {
    const localData = this.loadLocalData();
    const updated = localData.reports.map(report => {
      const matches =
        report.id === identifier || report.local_id === identifier || report.account_id === identifier;
      return matches ? updater(report) : report;
    });
    this.saveReports(updated);
    return updated;
  }

  private upsertView(views: UserView[], view: UserView): UserView[] {
    const key = this.viewKey(view);
    const existing = views.find(item => this.viewKey(item) === key);
    if (!existing) {
      return this.sortViews([...views, view]);
    }

    const merged: UserView = {
      ...existing,
      ...view,
      reference_no: view.reference_no,
      source_council: view.source_council || existing.source_council || DEFAULT_COUNCIL_SLUG,
      source_system: view.source_system || existing.source_system || 'idox',
      application_uid: view.application_uid || existing.application_uid,
      address: view.address || existing.address || '',
      category: view.category ?? existing.category,
      scale_rating: typeof view.scale_rating === 'number' ? view.scale_rating : existing.scale_rating,
      view_count: Math.max(existing.view_count ?? 1, view.view_count ?? 1),
      viewed_at:
        new Date(view.viewed_at).getTime() > new Date(existing.viewed_at).getTime()
          ? view.viewed_at
          : existing.viewed_at,
      sync_state:
        view.sync_state === 'account'
          ? 'account'
          : normalizeSyncState(view.sync_state, existing.sync_state || 'local_only'),
      synced_at: view.synced_at || existing.synced_at,
      last_sync_attempt_at: view.last_sync_attempt_at || existing.last_sync_attempt_at,
      last_sync_error: view.last_sync_error ?? existing.last_sync_error ?? null,
      local_id: view.local_id || existing.local_id,
    };

    const filtered = views.filter(item => this.viewKey(item) !== key);
    return this.sortViews([...filtered, merged]);
  }

  private updateViewState(
    referenceNo: string,
    updater: (view: UserView) => UserView,
    council = DEFAULT_COUNCIL_SLUG
  ): UserView[] {
    const key = this.viewKey({ reference_no: referenceNo, source_council: council });
    const localData = this.loadLocalData();
    const updated = localData.views.map(view =>
      this.viewKey(view) === key ? updater(view) : view
    );
    this.saveViews(updated);
    return updated;
  }

  private mergeFavorites(localFavorites: UserFavorite[], serverFavorites: UserFavorite[]): UserFavorite[] {
    const merged = new Map<string, UserFavorite>();

    localFavorites.forEach(favorite => {
      merged.set(this.favoriteKey(favorite), favorite);
    });

    serverFavorites.forEach(favorite => {
      const key = this.favoriteKey(favorite);
      const existing = merged.get(key);
      merged.set(key, {
        ...existing,
        ...favorite,
        reference_no: favorite.reference_no,
        source_council: favorite.source_council || existing?.source_council || DEFAULT_COUNCIL_SLUG,
        source_system: favorite.source_system || existing?.source_system || 'idox',
        application_uid: favorite.application_uid || existing?.application_uid,
        sync_state: 'account',
        last_sync_error: null,
        synced_at: favorite.synced_at || new Date().toISOString(),
        address: favorite.address || existing?.address || '',
        favorited_at: favorite.favorited_at || existing?.favorited_at || new Date().toISOString(),
      });
    });

    return this.sortFavorites(Array.from(merged.values()));
  }

  private mergeViews(localViews: UserView[], serverViews: UserView[]): UserView[] {
    const merged = new Map<string, UserView>();

    serverViews.forEach(view => {
      const key = this.viewKey(view);
      merged.set(key, { ...view, sync_state: 'account', last_sync_error: null });
    });

    localViews.forEach(view => {
      const key = this.viewKey(view);
      const existing = merged.get(key);
      if (!existing) {
        merged.set(key, { ...view });
        return;
      }

      const mergedCount = Math.max(existing.view_count ?? 1, view.view_count ?? 1);
      const localAhead = (view.view_count ?? 1) > (existing.view_count ?? 1);
      const latestViewedAt =
        new Date(view.viewed_at).getTime() > new Date(existing.viewed_at).getTime()
          ? view.viewed_at
          : existing.viewed_at;

      merged.set(key, {
        ...existing,
        ...view,
        reference_no: view.reference_no,
        source_council: view.source_council || existing.source_council || DEFAULT_COUNCIL_SLUG,
        source_system: view.source_system || existing.source_system || 'idox',
        application_uid: view.application_uid || existing.application_uid,
        address: view.address || existing.address || '',
        category: view.category ?? existing.category,
        scale_rating: typeof view.scale_rating === 'number' ? view.scale_rating : existing.scale_rating,
        view_count: mergedCount,
        viewed_at: latestViewedAt,
        sync_state: localAhead ? normalizeSyncState(view.sync_state, 'pending') : 'account',
        last_sync_error: localAhead ? view.last_sync_error ?? null : null,
        synced_at: localAhead ? view.synced_at : existing.synced_at || new Date().toISOString(),
        last_sync_attempt_at: view.last_sync_attempt_at || existing.last_sync_attempt_at,
        local_id: view.local_id || existing.local_id,
      });
    });

    return this.sortViews(Array.from(merged.values()));
  }

  private mergeReports(localReports: UserReport[], serverReports: UserReport[]): UserReport[] {
    const merged = new Map<string, UserReport>();

    localReports.forEach(report => {
      merged.set(this.reportKey(report), report);
    });

    serverReports.forEach(report => {
      const key = this.reportKey(report);
      const referenceKey = normalizeRef(report.reference_no) || report.reference_no;
      const existing =
        merged.get(key) ||
        localReports.find(local =>
          local.id === report.id ||
          local.account_id === report.id ||
          local.local_id === report.local_id ||
          (
            referenceKey &&
            (normalizeRef(local.reference_no) || local.reference_no) === referenceKey &&
            (local.kind || 'property') === (report.kind || 'property')
          )
        );
      const bestReportText =
        existing?.report && existing.report.length > report.report.length ? existing.report : report.report;

      if (existing) {
        merged.delete(this.reportKey(existing));
      }

      merged.set(key, {
        ...existing,
        ...report,
        id: report.id,
        account_id: report.account_id || report.id,
        local_id: existing?.local_id,
        report: bestReportText,
        sync_state: 'account',
        last_sync_error: null,
        synced_at: report.synced_at || existing?.synced_at || new Date().toISOString(),
      });
    });

    return this.sortReports(Array.from(merged.values()));
  }

  private async syncPendingFavorites(localFavorites: UserFavorite[]): Promise<UserFavorite[]> {
    if (!USE_API || !(await isUserAuthenticated())) {
      return localFavorites;
    }

    let working = [...localFavorites];
    for (const favorite of working.filter(item => item.sync_state !== 'account')) {
      try {
        const now = new Date().toISOString();
        await apiAddFavorite(favorite.reference_no, {
          council: favorite.source_council || DEFAULT_COUNCIL_SLUG,
        });
        const syncedFavorite: UserFavorite = {
          ...favorite,
          sync_state: 'account',
          synced_at: now,
          last_sync_attempt_at: now,
          last_sync_error: null,
        };
        working = this.upsertFavorite(working, syncedFavorite);
        trackActivityMetric({
          feature: 'favorite',
          action: 'sync_recovered',
          sync_state: 'account',
          authenticated: true,
          reference_no: favorite.reference_no,
        });
      } catch (error) {
        const telemetry = classifyAccountSyncFallback(error);
        const state = telemetry.state;
        const failedFavorite: UserFavorite = {
          ...favorite,
          sync_state: state,
          last_sync_attempt_at: new Date().toISOString(),
          last_sync_error: errorMessage(error) || 'Favorite sync failed',
        };
        working = this.upsertFavorite(working, failedFavorite);
        trackActivityMetric({
          feature: 'favorite',
          action: telemetry.action,
          sync_state: state,
          authenticated: telemetry.authenticated,
          reference_no: favorite.reference_no,
          council: favorite.source_council || DEFAULT_COUNCIL_SLUG,
          metadata: {
            fallback_reason: telemetry.metadata.fallback_reason,
            sync_error_status: telemetry.metadata.sync_error_status,
          },
        });
      }
    }

    this.saveFavorites(working);
    return working;
  }

  private async uploadReport(report: UserReport, action: 'saved_account' | 'sync_recovered'): Promise<UserReport> {
    const now = new Date().toISOString();
    const metadata = {
      ...(report.metadata || {}),
      clientLocalId: report.local_id || report.id,
      clientGeneratedAt: report.generated_at,
      recoveredFromLocalCache: action === 'sync_recovered',
    };

    const response = await createReport({
      title: report.title || `AI Planning Report - ${report.address || 'Unknown Address'}`,
      kind: report.kind || 'property',
      reference_no: report.reference_no,
      address: report.address,
      report_text: report.report,
      metadata,
    });

    const syncedReport: UserReport = {
      ...report,
      id: response.id,
      account_id: response.id,
      sync_state: 'account',
      synced_at: now,
      last_sync_attempt_at: now,
      last_sync_error: null,
      metadata,
    };

    const localData = this.loadLocalData();
    this.saveReports(this.upsertReport(localData.reports, syncedReport));

    trackActivityMetric({
      feature: 'report',
      action,
      sync_state: 'account',
      authenticated: true,
      reference_no: report.reference_no,
      metadata: { reportId: response.id },
    });

    return syncedReport;
  }

  private async syncPendingReports(localReports: UserReport[]): Promise<UserReport[]> {
    if (!USE_API || !(await isUserAuthenticated())) {
      return localReports;
    }

    let working = [...localReports];
    for (const report of working.filter(item => item.sync_state !== 'account')) {
      try {
        const syncedReport = await this.uploadReport(report, 'sync_recovered');
        working = this.upsertReport(working, syncedReport);
      } catch (error) {
        const telemetry = classifyAccountSyncFallback(error);
        const state = telemetry.state;
        const failedReport: UserReport = {
          ...report,
          sync_state: state,
          last_sync_attempt_at: new Date().toISOString(),
          last_sync_error: errorMessage(error) || 'Report sync failed',
        };
        working = this.upsertReport(working, failedReport);
        trackActivityMetric({
          feature: 'report',
          action: telemetry.action,
          sync_state: state,
          authenticated: telemetry.authenticated,
          reference_no: report.reference_no,
          metadata: telemetry.metadata,
        });
      }
    }

    this.saveReports(working);
    return working;
  }

  getCachedUserData(): UserData {
    return this.loadLocalData();
  }

  getSyncStatusSummary(): UserDataSyncSummary {
    const localData = this.loadLocalData();
    return buildUserDataSyncSummary(localData);
  }

  findStoredReport(identifier: string): UserReport | undefined {
    const localData = this.loadLocalData();
    return localData.reports.find(
      report => report.id === identifier || report.local_id === identifier || report.account_id === identifier
    );
  }

  async deleteStoredReport(identifier: string): Promise<boolean> {
    const localData = this.loadLocalData();
    const target = localData.reports.find(
      report => report.id === identifier || report.local_id === identifier || report.account_id === identifier
    );
    if (!target) return false;

    if (target.sync_state === 'account' && target.account_id) {
      try {
        const { deleteReport } = await import('../accountsReports');
        await deleteReport(target.account_id);
      } catch (error) {
        console.warn('Failed to delete report from account:', error);
        return false;
      }
    }

    const remaining = localData.reports.filter(
      report => report.id !== identifier && report.local_id !== identifier && report.account_id !== identifier
    );
    this.saveReports(remaining);
    return true;
  }

  async syncUserData(options: SyncOptions = {}): Promise<boolean> {
    if (this.syncInProgress) return false;
    const { forceServerState = false } = options;

    if (!USE_API) {
      console.debug('Server sync unavailable: NEXT_PUBLIC_API_URL not configured');
      return false;
    }

    const isAuthenticated = await isUserAuthenticated();
    if (!isAuthenticated) {
      console.debug('No authenticated user, skipping sync');
      return false;
    }

    this.syncInProgress = true;
    try {
      const currentLocalData = this.loadLocalData();
      await this.syncPendingFavorites(currentLocalData.favorites);
      await this.syncPendingReports(currentLocalData.reports);

      const results = await Promise.allSettled([
        this.hydrateFavorites(forceServerState),
        this.hydrateReports(),
        this.hydrateViews(),
      ]);

      const localData = this.loadLocalData();
      const favorites = results[0].status === 'fulfilled' ? results[0].value : localData.favorites;
      const reports = results[1].status === 'fulfilled' ? results[1].value : localData.reports;
      const views = results[2].status === 'fulfilled' ? results[2].value : localData.views;
      const settings = localData.settings || {};
      const lastSynced = new Date().toISOString();

      this.saveLocalData({ lastSynced });
      this.dispatchFavoritesUpdated(favorites);
      this.dispatchReportsUpdated(reports);
      this.dispatchViewsUpdated(views);
      this.dispatchSettingsUpdated(settings);

      return !results.every(result => result.status === 'rejected');
    } catch (error) {
      console.error('Failed to sync user data:', error);
      return false;
    } finally {
      this.syncInProgress = false;
    }
  }

  async hydrateFavorites(
    _forceServerState = false,
    council = DEFAULT_COUNCIL_SLUG
  ): Promise<UserFavorite[]> {
    if (!USE_API) {
      return this.loadLocalData().favorites;
    }
    if (this.isHydratingFavorites) {
      return this.loadLocalData().favorites;
    }

    this.isHydratingFavorites = true;
    try {
      if (!(await isUserAuthenticated())) {
        return this.loadLocalData().favorites;
      }

      let rows: unknown[] = [];
      try {
        rows = await apiListFavorites(council);
      } catch (error: unknown) {
        if (errorStatus(error) === 401) {
          return this.loadLocalData().favorites;
        }
        console.debug('Failed to hydrate favorites:', errorMessage(error) || 'Unknown error');
        return this.loadLocalData().favorites;
      }

      const now = new Date().toISOString();
      const serverFavorites = Array.isArray(rows)
        ? rows
            .map((row, index) => {
              const record = asRecord(row);
              if (!record) return null;
              const normalizedRef = normalizeRef(asString(record.reference_no) ?? '');
              if (!normalizedRef) return null;

              return {
                id: `api_fav_${index}_${normalizedRef}`,
                reference_no: normalizedRef,
                application_uid: asString(record.application_uid),
                source_council: asString(record.source_council) || this.councilScope(council),
                source_system: asString(record.source_system) || 'idox',
                address: asString(record.address) || '',
                favorited_at: normalizeTimestamp(record.created_at, now),
                category: asString(record.category),
                primaryCategory: asString(record.primaryCategory),
                application_data: record.application_data,
                sync_state: 'account' as const,
                synced_at: now,
                last_sync_error: null,
              };
            })
            .filter(Boolean) as UserFavorite[]
        : ([] as UserFavorite[]);

      const mergedFavorites = this.mergeFavorites(this.loadLocalData().favorites, serverFavorites);
      this.saveFavorites(mergedFavorites);
      return mergedFavorites;
    } finally {
      this.isHydratingFavorites = false;
    }
  }

  async addFavorite(
    referenceOrFavorite: Omit<UserFavorite, 'id' | 'favorited_at'> | string,
    note?: string
  ): Promise<boolean> {
    const rawReference =
      typeof referenceOrFavorite === 'string' ? referenceOrFavorite : referenceOrFavorite?.reference_no;
    const referenceNo = normalizeRef(rawReference ?? '');
    if (!referenceNo) return false;

    const isAuthenticated = USE_API ? await isUserAuthenticated() : false;
    const now = new Date().toISOString();
    const localData = this.loadLocalData();
    const favoriteSource = typeof referenceOrFavorite === 'string' ? {} as Partial<UserFavorite> : referenceOrFavorite;
    const council = this.councilScope(favoriteSource.source_council);
    const identityKey = this.favoriteKey({ reference_no: referenceNo, source_council: council });
    const existing = localData.favorites.find(
      favorite => this.favoriteKey(favorite) === identityKey
    );
    const favorite: UserFavorite = {
      ...existing,
      ...favoriteSource,
      id: existing?.id || `fav_${referenceNo}_${Date.now()}`,
      local_id: existing?.local_id || existing?.id || `fav_${referenceNo}_${Date.now()}`,
      reference_no: referenceNo,
      source_council: favoriteSource.source_council || council,
      source_system: favoriteSource.source_system || existing?.source_system || 'idox',
      application_uid: favoriteSource.application_uid || existing?.application_uid,
      address: existing?.address || favoriteSource.address || '',
      favorited_at: existing?.favorited_at || now,
      sync_state: !USE_API ? 'local_only' : (isAuthenticated ? 'pending' : 'local_only'),
      last_sync_attempt_at: now,
      last_sync_error: null,
    };

    const updatedFavorites = this.upsertFavorite(localData.favorites, favorite);
    this.saveFavorites(updatedFavorites);
    const favoriteSyncState: SyncState = favorite.sync_state ?? 'local_only';

    trackActivityMetric({
      feature: 'favorite',
      action: favoriteSyncState === 'pending' ? 'sync_pending' : 'saved_local',
      sync_state: favoriteSyncState,
      authenticated: isAuthenticated,
      reference_no: referenceNo,
      council,
    });
    trackCoreJourneyEvent('engagement', 'save_clicked', {
      source: 'user_data_service',
      placement: 'favorite',
      entityType: 'planning_application',
      entityId: referenceNo,
      status: favoriteSyncState,
    });

    if (!USE_API || !isAuthenticated) {
      return true;
    }

    try {
      await apiAddFavorite(referenceNo, { note, council });
      this.updateFavoriteState(referenceNo, current => ({
        ...current,
        sync_state: 'account',
        synced_at: new Date().toISOString(),
        last_sync_attempt_at: new Date().toISOString(),
        last_sync_error: null,
      }), council);
      trackActivityMetric({
        feature: 'favorite',
        action: 'saved_account',
        sync_state: 'account',
        authenticated: true,
        reference_no: referenceNo,
        council,
      });
      void this.hydrateFavorites(false, council);
    } catch (error) {
      const telemetry = classifyAccountSyncFallback(error);
      const state = telemetry.state;
      this.updateFavoriteState(referenceNo, current => ({
        ...current,
        sync_state: state,
        last_sync_attempt_at: new Date().toISOString(),
        last_sync_error: errorMessage(error) || 'Favorite sync failed',
      }), council);
      trackActivityMetric({
        feature: 'favorite',
        action: telemetry.action,
        sync_state: state,
        authenticated: telemetry.authenticated,
        reference_no: referenceNo,
        council,
        metadata: telemetry.metadata,
      });
    }

    return true;
  }

  async removeFavorite(
    referenceNo: string,
    council = DEFAULT_COUNCIL_SLUG
  ): Promise<boolean> {
    const normalizedRef = normalizeRef(referenceNo ?? '');
    if (!normalizedRef) return false;
    const councilSlug = this.councilScope(council);
    const identityKey = this.favoriteKey({
      reference_no: normalizedRef,
      source_council: councilSlug,
    });

    const localData = this.loadLocalData();
    const existing = localData.favorites.find(
      favorite => this.favoriteKey(favorite) === identityKey
    );
    if (!existing) return false;

    const remaining = localData.favorites.filter(
      favorite => this.favoriteKey(favorite) !== identityKey
    );
    this.saveFavorites(remaining);

    if (!USE_API || existing.sync_state !== 'account') {
      return true;
    }

    try {
      await apiRemoveFavorite(normalizedRef, councilSlug);
    } catch (error) {
      console.warn(`Failed to remove favorite ${normalizedRef}:`, error);
      const restoredFavorite: UserFavorite = {
        ...existing,
        sync_state: 'failed',
        last_sync_attempt_at: new Date().toISOString(),
        last_sync_error: errorMessage(error) || 'Favorite removal failed',
      };
      this.saveFavorites(this.upsertFavorite(this.loadLocalData().favorites, restoredFavorite));
      return false;
    }

    return true;
  }

  async persistGeneratedReport(input: PersistGeneratedReportInput): Promise<PersistGeneratedReportResult> {
    const normalizedRef = normalizeRef(input.reference_no ?? '') || input.reference_no || 'unknown';
    const localId = `report_${normalizedRef}_${Date.now()}`;
    const generatedAt = new Date().toISOString();
    const isAuthenticated = USE_API ? await isUserAuthenticated() : false;

    const localReport: UserReport = {
      id: localId,
      local_id: localId,
      reference_no: normalizedRef,
      address: input.address || '',
      report: input.report,
      generated_at: generatedAt,
      application_data: input.application_data,
      metadata: input.metadata || null,
      title: input.title || `AI Planning Report - ${input.address || 'Unknown Address'}`,
      kind: input.kind || 'property',
      sync_state: !USE_API ? 'local_only' : (isAuthenticated ? 'pending' : 'local_only'),
      last_sync_attempt_at: generatedAt,
      last_sync_error: null,
    };

    this.saveReports(this.upsertReport(this.loadLocalData().reports, localReport));
    const localReportSyncState: SyncState = localReport.sync_state ?? 'local_only';

    trackActivityMetric({
      feature: 'report',
      action: localReportSyncState === 'pending' ? 'sync_pending' : 'saved_local',
      sync_state: localReportSyncState,
      authenticated: isAuthenticated,
      reference_no: normalizedRef,
    });
    trackCoreJourneyEvent('engagement', 'save_clicked', {
      source: 'user_data_service',
      placement: 'planning_report',
      entityType: 'planning_application',
      entityId: normalizedRef,
      status: localReportSyncState,
    });

    if (!USE_API || !isAuthenticated) {
      return {
        report: localReport,
        syncState: localReportSyncState,
        savedToAccount: false,
        message: 'Report saved on this device. Sign in to sync it to your account.',
      };
    }

    try {
      const syncedReport = await this.uploadReport(localReport, 'saved_account');
      return {
        report: syncedReport,
        syncState: 'account',
        savedToAccount: true,
        message: 'Report saved to your account.',
      };
    } catch (error) {
      const telemetry = classifyAccountSyncFallback(error);
      const state = telemetry.state;
      const failedReport: UserReport = {
        ...localReport,
        sync_state: state,
        last_sync_attempt_at: new Date().toISOString(),
        last_sync_error: errorMessage(error) || 'Report sync failed',
      };
      this.saveReports(this.upsertReport(this.loadLocalData().reports, failedReport));

      trackActivityMetric({
        feature: 'report',
        action: telemetry.action,
        sync_state: state,
        authenticated: telemetry.authenticated,
        reference_no: normalizedRef,
        metadata: telemetry.metadata,
      });

      return {
        report: failedReport,
        syncState: state,
        savedToAccount: false,
        message:
          state === 'local_only'
            ? 'Report saved on this device. Sign in to sync it to your account.'
            : 'Report saved locally. Account sync failed and will be retried automatically.',
      };
    }
  }

  async addReport(report: Omit<UserReport, 'id' | 'generated_at'>): Promise<boolean> {
    await this.persistGeneratedReport({
      reference_no: report.reference_no,
      address: report.address,
      report: report.report,
      application_data: report.application_data,
      metadata: report.metadata || undefined,
      title: report.title,
      kind: report.kind,
    });
    return true;
  }

  async addView(view: Omit<UserView, 'id' | 'viewed_at' | 'view_count'>): Promise<boolean> {
    const normalizedRef = normalizeRef(view?.reference_no ?? '');
    if (!normalizedRef) return false;
    const council = this.councilScope(view.source_council);
    const identityKey = this.viewKey({ reference_no: normalizedRef, source_council: council });

    const isAuthenticated = USE_API ? await isUserAuthenticated() : false;
    const now = new Date().toISOString();
    const localData = this.loadLocalData();
    const existing = localData.views.find(item => this.viewKey(item) === identityKey);
    const nextSyncState: SyncState = !USE_API ? 'local_only' : (isAuthenticated ? 'pending' : 'local_only');
    const nextView: UserView = {
      ...existing,
      ...view,
      id: existing?.id || `view_${normalizedRef}_${Date.now()}`,
      local_id: existing?.local_id || existing?.id || `view_${normalizedRef}_${Date.now()}`,
      reference_no: normalizedRef,
      source_council: view.source_council || council,
      source_system: view.source_system || existing?.source_system || 'idox',
      application_uid: view.application_uid || existing?.application_uid,
      address: view.address || existing?.address || '',
      category: view.category ?? existing?.category,
      scale_rating: typeof view.scale_rating === 'number' ? view.scale_rating : existing?.scale_rating,
      view_count: (existing?.view_count ?? 0) + 1,
      viewed_at: now,
      sync_state: nextSyncState,
      last_sync_attempt_at: now,
      last_sync_error: null,
    };

    this.saveViews(this.upsertView(localData.views, nextView));
    const nextViewSyncState: SyncState = nextView.sync_state ?? nextSyncState;

    trackActivityMetric({
      feature: 'view',
      action: nextViewSyncState === 'pending' ? 'sync_pending' : 'saved_local',
      sync_state: nextViewSyncState,
      authenticated: isAuthenticated,
      reference_no: normalizedRef,
      council,
    });

    return true;
  }

  markViewSynced(referenceNo: string, council = DEFAULT_COUNCIL_SLUG): void {
    const normalizedRef = normalizeRef(referenceNo ?? '');
    if (!normalizedRef) return;

    this.updateViewState(normalizedRef, current => ({
      ...current,
      sync_state: 'account',
      synced_at: new Date().toISOString(),
      last_sync_attempt_at: new Date().toISOString(),
      last_sync_error: null,
    }), council);

    trackActivityMetric({
      feature: 'view',
      action: 'saved_account',
      sync_state: 'account',
      authenticated: true,
      reference_no: normalizedRef,
    });
  }

  markViewSyncFailure(referenceNo: string, error?: unknown, council = DEFAULT_COUNCIL_SLUG): void {
    const normalizedRef = normalizeRef(referenceNo ?? '');
    if (!normalizedRef) return;

    const telemetry = classifyAccountSyncFallback(error);
    const state = telemetry.state;
    this.updateViewState(normalizedRef, current => ({
      ...current,
      sync_state: state,
      last_sync_attempt_at: new Date().toISOString(),
      last_sync_error: errorMessage(error) || 'View sync failed',
    }), council);

    trackActivityMetric({
      feature: 'view',
      action: telemetry.action,
      sync_state: state,
      authenticated: telemetry.authenticated,
      reference_no: normalizedRef,
      metadata: telemetry.metadata,
    });
  }

  async updateSettings(settings: Partial<UserSettings>): Promise<boolean> {
    const localData = this.loadLocalData();
    const updatedSettings = { ...localData.settings, ...settings };
    this.saveLocalData({ settings: updatedSettings });
    this.dispatchSettingsUpdated(updatedSettings);
    return true;
  }

  async initialize(): Promise<boolean> {
    if (!USE_API) {
      return false;
    }
    if (!(await isUserAuthenticated())) {
      return false;
    }
    return this.syncUserData();
  }

  async hydrateViews(council = DEFAULT_COUNCIL_SLUG): Promise<UserView[]> {
    if (!USE_API) {
      return this.loadLocalData().views;
    }
    if (!(await isUserAuthenticated())) {
      return this.loadLocalData().views;
    }

    let rows: unknown[] = [];
    try {
      rows = await apiListViews(100, council);
    } catch (error: unknown) {
      if (errorStatus(error) === 401) {
        return this.loadLocalData().views;
      }
      console.debug('Failed to hydrate views:', errorMessage(error) || 'Unknown error');
      return this.loadLocalData().views;
    }

    const now = new Date().toISOString();
      const serverViews = Array.isArray(rows)
        ? rows
            .map((row, index) => {
            const record = asRecord(row);
            if (!record) return null;
            const normalizedRef = normalizeRef(asString(record.reference_no) ?? '');
            if (!normalizedRef) return null;
            const context = asRecord(record.context);
            const numericCount = asFiniteNumber(record.view_count);
            const fallbackCount = Number(record.view_count);

            return {
              id: `api_view_${index}_${normalizedRef}`,
              reference_no: normalizedRef,
              application_uid: asString(record.application_uid),
              source_council: asString(record.source_council) || this.councilScope(council),
              source_system: asString(record.source_system) || 'idox',
              address: asString(record.address) || '',
              viewed_at: normalizeTimestamp(
                record.last_viewed_at ?? record.first_viewed_at,
                now
              ),
              category: asString(context?.category),
              scale_rating: asFiniteNumber(context?.scale_rating),
              view_count:
                numericCount ??
                (Number.isFinite(fallbackCount) && fallbackCount > 0 ? fallbackCount : 1),
              sync_state: 'account' as const,
                synced_at: now,
                last_sync_error: null,
              };
            })
          .filter(Boolean) as UserView[]
      : ([] as UserView[]);

    const mergedViews = this.mergeViews(this.loadLocalData().views, serverViews);
    this.saveViews(mergedViews);
    return mergedViews;
  }

  async hydrateReports(): Promise<UserReport[]> {
    const localData = this.loadLocalData();
    if (!USE_API) {
      return localData.reports;
    }
    if (!(await isUserAuthenticated())) {
      return localData.reports;
    }

    const afterPendingSync = await this.syncPendingReports(localData.reports);

    let rows: unknown[] = [];
    try {
      rows = await apiListReports(100);
    } catch (error: unknown) {
      if (errorStatus(error) === 401) {
        return this.loadLocalData().reports;
      }
      console.debug('Failed to hydrate reports:', errorMessage(error) || 'Unknown error');
      return this.loadLocalData().reports;
    }

    const now = new Date().toISOString();
    const serverReports = Array.isArray(rows)
      ? rows
          .map((row, index) => {
            const record = asRecord(row);
            if (!record) return null;
            const referenceNo = asString(record.reference_no) || '';
            const id = asString(record.id) || `api_report_${index}_${referenceNo || 'unknown'}`;

            return {
              id,
              account_id: id,
              reference_no: referenceNo,
              address: asString(record.address) || '',
              report:
                asString(record.report_text) ||
                asString(record.preview) ||
                '',
              generated_at: normalizeTimestamp(record.created_at, now),
              metadata: asRecord(record.metadata),
              title: asString(record.title),
              kind: asString(record.kind),
              sync_state: 'account' as const,
              synced_at: now,
              last_sync_error: null,
            };
          })
          .filter(Boolean) as UserReport[]
      : ([] as UserReport[]);

    const mergedReports = this.mergeReports(afterPendingSync, serverReports);
    this.saveReports(mergedReports);
    return mergedReports;
  }

  clearLocalData(): void {
    if (typeof window === 'undefined') return;
    try {
      localStorage.removeItem('cs_favorites');
      localStorage.removeItem('planning_reports');
      localStorage.removeItem('cs_views');
      localStorage.removeItem('user_settings');
      localStorage.removeItem('last_sync');
      localStorage.removeItem('cs_activity_client_id');
      localStorage.removeItem('cs_favorites_overlay_added');
      localStorage.removeItem('cs_favorites_overlay_removed');
    } catch (error) {
      console.error('Failed to clear local data:', error);
    }
  }

  async clearAllUserData(): Promise<boolean> {
    let apiSuccess = true;
    const apiErrors: string[] = [];

    try {
      if (USE_API && (await isUserAuthenticated())) {
        try {
          const [{ clearViews }, { clearReports, deleteReport }, { listFavorites, removeFavorite }] = await Promise.all([
            import('../views'),
            import('../accountsReports'),
            import('../favorites'),
          ]);

          try {
            const favorites = await listFavorites();
            for (const favorite of favorites) {
              if (!favorite?.reference_no) continue;
              try {
                await removeFavorite(favorite.reference_no);
              } catch (error: unknown) {
                apiSuccess = false;
                apiErrors.push(`favorite:${favorite.reference_no}:${errorMessage(error) || 'Unknown error'}`);
              }
            }
          } catch (error: unknown) {
            apiSuccess = false;
            apiErrors.push(`favorites:${errorMessage(error) || 'Unknown error'}`);
          }

          try {
            const reports = await apiListReports(100);
            for (const report of reports) {
              const reportRecord = asRecord(report);
              const reportId = asString(reportRecord?.id);
              if (!reportId) continue;
              try {
                await deleteReport(reportId);
              } catch (error: unknown) {
                apiSuccess = false;
                apiErrors.push(`report:${reportId}:${errorMessage(error) || 'Unknown error'}`);
              }
            }
          } catch (error: unknown) {
            apiSuccess = false;
            apiErrors.push(`reports:${errorMessage(error) || 'Unknown error'}`);
          }

          try {
            await clearViews();
          } catch (error: unknown) {
            apiSuccess = false;
            apiErrors.push(`views:${errorMessage(error) || 'Unknown error'}`);
          }

          try {
            await clearReports();
          } catch (error: unknown) {
            apiSuccess = false;
            apiErrors.push(`clearReports:${errorMessage(error) || 'Unknown error'}`);
          }
        } catch (error: unknown) {
          apiSuccess = false;
          apiErrors.push(`imports:${errorMessage(error) || 'Unknown error'}`);
        }
      }

      this.clearLocalData();
      this.dispatchFavoritesUpdated([]);
      this.dispatchViewsUpdated([]);
      this.dispatchReportsUpdated([]);

      if (!apiSuccess) {
        console.warn('Local data cleared, but some API data may remain:', apiErrors);
      }

      return true;
    } catch (error) {
      console.error('Failed to clear user data:', error);
      return false;
    }
  }
}

export const userDataService = new UserDataService();
export default userDataService;
